import { Info, Lock, Send } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Surface } from "@/components/surface";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { formatMoney } from "../../timeTracking/utils";
import { PortalAPI } from "../api/portalApi";
import { usePortal } from "../components/PortalShell";
import {
  emptyRequest,
  estimateText,
  nextSteps,
  validateRequest,
  type RequestErrors
} from "../portalModel";
import { refreshPortal, useBudgetImpact, useRequestForm } from "../queries";
import { PORTAL_PATH } from "../routes";
import type { RequestForm, RequestInput } from "../types";

const OTHER = "__other__";

const Field: FC<{
  id: string;
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}> = ({ id, label, error, className, children }) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <Label htmlFor={id} className="text-sm font-normal text-muted-foreground">
      {label}
    </Label>
    {children}
    {error ? <span className="text-xs text-destructive">{error}</span> : null}
  </div>
);

const Card: FC<{ title?: string; children: ReactNode; className?: string }> = ({
  title,
  children,
  className
}) => (
  <Surface className={cn("gap-3 p-4", className)}>
    {title ? <span className="text-base font-semibold">{title}</span> : null}
    {children}
  </Surface>
);

const KV: FC<{ k: string; children: ReactNode }> = ({ k, children }) => (
  <span className="flex gap-3 text-base">
    <span className="w-27.5 shrink-0 text-muted-foreground">{k}</span>
    <span className="flex min-w-0 items-center gap-1.5">{children}</span>
  </span>
);

const Pick: FC<{
  id: string;
  value: string;
  options: Array<{ id: string; label: string }>;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ id, value, options, onChange, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger id={id} className="h-8 w-full text-base">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.id} value={o.id}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const RequestCaseForm: FC<{
  form: RequestForm;
  companyName: string;
  firmName: string;
  canSeeSpend: boolean;
  onSubmit: (input: RequestInput) => Promise<void>;
  onCancel: () => void;
}> = ({ form, companyName, firmName, canSeeSpend, onSubmit, onCancel }) => {
  const [input, setInput] = useState<RequestInput>(() => emptyRequest(form));
  const [errors, setErrors] = useState<RequestErrors>({});
  const [otherJob, setOtherJob] = useState(form.jobs.length === 0);
  const [sending, setSending] = useState(false);
  const set = (patch: Partial<RequestInput>) =>
    setInput((prev) => ({ ...prev, ...patch }));
  const type = form.case_types.find((t) => t.case_type === input.case_type);
  const impact = useBudgetImpact(
    input.cost_center,
    input.case_type,
    canSeeSpend
  ).data;

  const submit = async () => {
    const found = validateRequest(input, form);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSending(true);
    try {
      await onSubmit(input);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <Card className="gap-3.5 p-4.5">
        <span className="text-base font-semibold">Employee</span>
        <div className="grid grid-cols-2 gap-3">
          <Field id="req-name" label="Full name" error={errors.employee_name}>
            <Input
              id="req-name"
              value={input.employee_name}
              onChange={(e) => set({ employee_name: e.currentTarget.value })}
              className="h-8"
            />
          </Field>
          <Field
            id="req-email"
            label="Work email"
            error={errors.employee_email}
          >
            <Input
              id="req-email"
              type="email"
              value={input.employee_email}
              onChange={(e) => set({ employee_email: e.currentTarget.value })}
              className="h-8"
            />
          </Field>
          <Field id="req-job" label="Job title">
            {otherJob ? (
              <Input
                id="req-job"
                value={input.job_title}
                onChange={(e) => set({ job_title: e.currentTarget.value })}
                className="h-8"
              />
            ) : (
              <Pick
                id="req-job"
                value={input.job_description_id}
                placeholder="From your job descriptions"
                options={[
                  ...form.jobs.map((j) => ({
                    id: j.id,
                    label: j.soc_code
                      ? `${j.title} · SOC ${j.soc_code}`
                      : j.title
                  })),
                  { id: OTHER, label: "Something else" }
                ]}
                onChange={(v) =>
                  v === OTHER
                    ? (setOtherJob(true), set({ job_description_id: "" }))
                    : set({ job_description_id: v })
                }
              />
            )}
          </Field>
          <Field id="req-worksite" label="Worksite">
            <Pick
              id="req-worksite"
              value={input.worksite_id}
              placeholder={
                form.worksites.length ? "Pick a worksite" : "None on file"
              }
              options={form.worksites.map((w) => ({
                id: w.id,
                label: w.remote ? `${w.name || "Remote"} (remote)` : w.name
              }))}
              onChange={(v) => set({ worksite_id: v })}
            />
          </Field>
          <Field
            id="req-cost-center"
            label="Department and cost center"
            error={errors.cost_center}
          >
            {form.cost_centers.length ? (
              <Pick
                id="req-cost-center"
                value={input.cost_center}
                placeholder="Pick a cost center"
                options={form.cost_centers.map((c) => ({
                  id: c.cost_center,
                  label: c.name ? `${c.name} · ${c.cost_center}` : c.cost_center
                }))}
                onChange={(v) => set({ cost_center: v })}
              />
            ) : (
              <Input
                id="req-cost-center"
                value={input.cost_center}
                onChange={(e) => set({ cost_center: e.currentTarget.value })}
                className="h-8 font-mono"
              />
            )}
          </Field>
          {form.entities.length > 1 ? (
            <Field id="req-entity" label="Employing entity">
              <Pick
                id="req-entity"
                value={input.entity_id}
                placeholder="Pick an entity"
                options={form.entities.map((e) => ({
                  id: e.id,
                  label: e.legal_name
                }))}
                onChange={(v) => set({ entity_id: v })}
              />
            </Field>
          ) : null}
        </div>

        <span className="pt-1 text-base font-semibold">What they need</span>
        <div
          className="grid grid-cols-2 gap-2.5 md:grid-cols-3"
          role="radiogroup"
          aria-label="Case type"
        >
          {form.case_types.map((t) => {
            const on = t.case_type === input.case_type;
            return (
              <button
                key={t.case_type}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => set({ case_type: t.case_type })}
                className={cn(
                  "flex flex-col gap-1 rounded-lg p-2.5 text-left transition-colors",
                  on ? "bg-accent-soft" : "bg-fill-1 hover:bg-fill-2"
                )}
              >
                <span className="flex items-center gap-2 text-base font-semibold">
                  <span
                    className={cn(
                      "flex size-3.5 shrink-0 items-center justify-center rounded-full border",
                      on ? "border-primary" : "border-line-4"
                    )}
                  >
                    {on ? (
                      <span className="size-1.5 rounded-full bg-primary" />
                    ) : null}
                  </span>
                  {t.label}
                </span>
                <span className="pl-5.5 text-xs text-muted-foreground">
                  {estimateText(t)}
                </span>
              </button>
            );
          })}
        </div>
        {errors.case_type ? (
          <span className="text-xs text-destructive">{errors.case_type}</span>
        ) : null}
        <span className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          Not sure? Pick the closest and add a note. We&apos;ll confirm the
          right case before any work starts.
        </span>
        <Field id="req-notes" label="Notes for the firm">
          <Textarea
            id="req-notes"
            rows={2}
            value={input.notes}
            placeholder="Current status, start date, anything we should know"
            onChange={(e) => set({ notes: e.currentTarget.value })}
            className="text-base"
          />
        </Field>

        <span className="pt-1 text-base font-semibold">Billing</span>
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="req-po"
            label={form.requires_po ? "PO number" : "PO number (optional)"}
            error={errors.po}
          >
            <Input
              id="req-po"
              value={input.po}
              onChange={(e) => set({ po: e.currentTarget.value })}
              className="h-8 font-mono"
            />
          </Field>
          <Field
            id="req-matter"
            label={
              form.requires_client_matter_id
                ? `${companyName} matter ID`
                : `${companyName} matter ID (optional)`
            }
            error={errors.client_matter_id}
          >
            <Input
              id="req-matter"
              value={input.client_matter_id}
              placeholder={form.client_matter_id_format ?? undefined}
              onChange={(e) => set({ client_matter_id: e.currentTarget.value })}
              className="h-8 font-mono"
            />
          </Field>
        </div>
        <span className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sending}
            onClick={() => void submit()}
          >
            <Send /> Send request
          </Button>
        </span>
      </Card>

      <div className="flex flex-col gap-3">
        <Card title="Estimate">
          {type ? (
            <>
              <KV k="Legal fee">{estimateText(type)}</KV>
              {type.bill_on ? <KV k="Billed">{type.bill_on}</KV> : null}
              <KV k="Paid by">
                {type.lock ? (
                  <Lock className="size-3 text-muted-foreground" />
                ) : null}
                {companyName} {type.employer_share}%
                {type.lock ? (
                  <span className="text-xs text-muted-foreground">
                    required by law
                  </span>
                ) : null}
              </KV>
              <KV k="USCIS fees">Passed through at cost by the firm</KV>
            </>
          ) : (
            <span className="text-base text-muted-foreground">
              Pick what they need to see the fee.
            </span>
          )}
          {impact ? (
            <div
              className="flex flex-col gap-1.5 pt-1"
              data-testid="budget-impact"
            >
              <span className="text-sm text-muted-foreground">
                {impact.name || impact.cost_center} budget after this case
              </span>
              <Progress
                value={Math.min(
                  100,
                  ((impact.spent + impact.estimate) /
                    Math.max(impact.budget, 1)) *
                    100
                )}
              />
              <span
                className={cn(
                  "text-sm tabular-nums",
                  impact.left_after < 0 && "text-destructive"
                )}
              >
                {formatMoney(impact.left_after)} left of{" "}
                {formatMoney(impact.budget)}
              </span>
            </div>
          ) : null}
        </Card>
        <Card title="What happens next">
          {nextSteps(form, firmName, input.employee_name, companyName).map(
            (t, i) => (
              <span key={t} className="flex gap-2.5 text-base leading-4.75">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold">
                  {i + 1}
                </span>
                {t}
              </span>
            )
          )}
        </Card>
      </div>
    </div>
  );
};

export const PortalRequestPage: FC = () => {
  const me = usePortal();
  const navigate = useNavigate();
  const { data: form, isLoading } = useRequestForm(me.features.requests);
  const submit = async (input: RequestInput) => {
    try {
      const created = await PortalAPI.createRequest(input);
      toast(
        created.status === "pending_firm"
          ? `Sent to ${me.firm.name}. You're an approver, so it's already approved.`
          : `Sent to ${
              me.approver_names.join(" or ") || "your approver"
            } to approve`
      );
      void refreshPortal();
      navigate(PORTAL_PATH);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't send the request"
      );
    }
  };
  return (
    <>
      <span className="flex flex-col gap-1">
        <span className="text-xl font-semibold">Request a case</span>
        <span className="text-base text-muted-foreground">
          Tell us who and what. We&apos;ll confirm, open the case and send the
          employee their questionnaire.
        </span>
      </span>
      {isLoading || !form ? (
        <Skeleton className="h-96" />
      ) : (
        <RequestCaseForm
          form={form}
          companyName={me.company.name}
          firmName={me.firm.name}
          canSeeSpend={me.features.spend}
          onSubmit={submit}
          onCancel={() => navigate(PORTAL_PATH)}
        />
      )}
    </>
  );
};
